import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { useAppContext } from 'providers/AppProvider';
import CardDropdown from 'components/common/CardDropdown';
import { Dropdown } from 'react-bootstrap';
import Flex from '../../common/Flex';
import { addSportsHall, editSportsHall, listSportsHall } from '../../../lib/api/club';

const canManageSportsHalls = user => ['admin', 'operator'].includes(user?.role_type_id);

const SportsHallForm = ({ initialData, onClose, onSave }) => {
  const isEditing = Boolean(initialData);
  const [hall, setHall] = useState(initialData || { name: '', city: '', sorting_number: '' });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = event => {
    const { name, value } = event.target;
    setHall(current => ({ ...current, [name]: value }));
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const name = hall.name?.trim() || '';
    const hasSortingNumber = String(hall.sorting_number).trim() !== '';
    const sortingNumber = Number(hall.sorting_number);
    const nextErrors = {
      name: !name,
      sorting_number: hasSortingNumber && (!Number.isInteger(sortingNumber) || sortingNumber < 0)
    };

    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    const changes = {};
    if (!isEditing || name !== initialData.name) changes.name = name;
    if (!isEditing || (hall.city || '') !== (initialData.city || '')) changes.city = hall.city || '';
    if (hasSortingNumber && (!isEditing || sortingNumber !== Number(initialData.sorting_number))) {
      changes.sorting_number = sortingNumber;
    }

    if (isEditing && Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    setIsSaving(true);
    setSubmitError('');
    try {
      if (isEditing) {
        await editSportsHall(initialData.sports_hall_id, changes);
      } else {
        await addSportsHall(changes);
      }
      await onSave();
      onClose();
    } catch (error) {
      setSubmitError(error.body?.message || 'Izmjene nije bilo moguće sačuvati. Pokušajte ponovo.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <Form.Group className="mb-3" controlId="sports-hall-name">
        <Form.Label>Naziv</Form.Label>
        <Form.Control required name="name" value={hall.name || ''} onChange={handleChange} isInvalid={errors.name} />
        <Form.Control.Feedback type="invalid">Naziv je obavezan.</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="sports-hall-city">
        <Form.Label>Grad</Form.Label>
        <Form.Control name="city" value={hall.city || ''} onChange={handleChange} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="sports-hall-sorting-number">
        <Form.Label>Redni broj</Form.Label>
        <Form.Control min="0" step="1" type="number" name="sorting_number" value={hall.sorting_number ?? ''} onChange={handleChange} isInvalid={errors.sorting_number} placeholder={isEditing ? undefined : "Automatski"} />
        <Form.Text className="text-muted">Ako ga izostavite, API postavlja sljedeći broj.</Form.Text>
        <Form.Control.Feedback type="invalid">Unesite cijeli broj jednak ili veći od nule.</Form.Control.Feedback>
      </Form.Group>
      {submitError && <div className="text-danger mb-3">{submitError}</div>}
      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>Otkaži</Button>
        <Button type="submit" disabled={isSaving}>{isSaving ? 'Čuvanje...' : 'Sačuvaj'}</Button>
      </div>
    </Form>
  );
};

export const List = () => {
  const { userApp } = useAppContext();
  const [sportsHalls, setSportsHalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedHall, setSelectedHall] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const getSportsHalls = useCallback(async () => {
    setIsLoading(true);
    try {
      const halls = await listSportsHall();
      setSportsHalls(halls);
    } catch (error) {
      console.error('Failed to load sports halls', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getSportsHalls();
  }, [getSportsHalls]);

  const renderActions = hall => (
    <CardDropdown>
      <div className="py-2">
        <Dropdown.Item onClick={() => { setSelectedHall(hall); setShowModal(true); }}>Izmijeni</Dropdown.Item>
      </div>
    </CardDropdown>
  );

  const canManage = canManageSportsHalls(userApp);

  return (
    <Card className={classNames('h-100')}>
      <Card.Header>
        <Row className="g-2">
          <Col><Flex><h5 className="mb-0">Sportske sale</h5></Flex></Col>
          {canManageSportsHalls(userApp) && (
            <Col xs="auto"><Button onClick={() => { setSelectedHall(null); setShowModal(true); }}>Dodaj sportsku salu</Button></Col>
          )}
        </Row>
      </Card.Header>
      <Card.Body className="bg-body-tertiary">
        {isLoading ? (
          <div className="text-center"><Spinner animation="border" role="status" /></div>
        ) : (
          <Row className="mb-3">
            <Col xs={12}>
              <Table responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Naziv</th>
                    <th>Grad</th>
                    <th>Redni broj</th>
                    {canManage && <th className="text-end">Akcija</th>}
                  </tr>
                </thead>
                <tbody>
                  {sportsHalls.map(hall => (
                    <tr key={hall.sports_hall_id}>
                      <td>{hall.name}</td>
                      <td>{hall.city || '-'}</td>
                      <td>{hall.sorting_number}</td>
                      {canManage && <td className="text-end">{renderActions(hall)}</td>}
                    </tr>
                  ))}
                  {sportsHalls.length === 0 && (
                    <tr><td className="text-center text-muted" colSpan={canManage ? 4 : 3}>Nema unesenih sportskih sala.</td></tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        )}
      </Card.Body>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton><Modal.Title>{selectedHall ? 'Izmijeni sportsku salu' : 'Dodaj sportsku salu'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <SportsHallForm initialData={selectedHall} onClose={() => setShowModal(false)} onSave={getSportsHalls} />
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default List;
